import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Article = ({ language = 'de' }) => {
  const navigate = useNavigate();

  // Scroll vers le haut au chargement de la page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    // Marquer dans sessionStorage que l'utilisateur revient de la page article
    sessionStorage.setItem('timeline_from_article_page', 'true');
    console.log('Article - sessionStorage timeline_from_article_page défini à true');
    navigate(-1);
  };

  return (
    <div className="article-page">
      <div className="article-container">
        {/* Header avec bouton retour */}
        <div className="article-header">
          <button className="article-back-btn" onClick={handleBack}>
            <img src="/images/back.png" alt="Back" className="back-icon" />
            <span className="back-label">Zurück</span>
          </button>
          <h1 className="article-title">Intervallfasten für den Alltag: Ein praktischer Leitfaden</h1>
        </div>

        {/* Image principale */}
        <div className="article-hero-image">
          <img src="/images/interval-fasten-bild-1.webp" alt="Intervallfasten" className="hero-img" />
        </div>

        {/* Contenu de l'article */}
        <div className="article-content">
          
          {/* Einleitung */}
          <section className="article-section">
            <h2 className="section-title">Einleitung</h2>
            <p className="article-text">
              Intermittierendes Fasten (IF) hat aufgrund seiner Auswirkungen auf den Stoffwechsel, die Gewichtsregulierung und Entzündungen große Aufmerksamkeit erregt. Wir von Buchinger Wilhelmi empfehlen eine Variante des Intervallfastens, die schonend, nachhaltig und mit einem erfüllten Alltag vereinbar ist. Diese Richtlinien richten sich an Erwachsene, die bei guter Gesundheit sind oder ihre Gesundheit stärken und Krankheiten vorbeugen möchten. Sie wurzeln in drei Grundprinzipien: Medizin, ökologische Nachhaltigkeit und ethische Verantwortung.
            </p>
            <p className="article-text">
              Wir schlagen einen 14:10-Rhythmus vor – 14 Stunden Fasten und ein 10-stündiges Essensfenster – strukturiert um drei regelmäßige Mahlzeiten, ohne Snacks und ohne Auslassen von Frühstück oder Abendessen. Warum? Rhythmus, Regelmäßigkeit und Genuss sind die wichtigsten Zutaten für langfristigen Erfolg.
            </p>
          </section>

          {/* Warum intermittierendes Fasten */}
          <section className="article-section">
            <h2 className="section-title">Warum intermittierendes Fasten?</h2>
            <p className="article-text">
              Fasten ist mehr als Kalorienrestriktion. Es ist eine Zeit für den Körper, sich auszuruhen, zurückzusetzen und zu reparieren. Während des Fastenfensters verschiebt sich unser Stoffwechsel in Richtung Fettverbrennung, der Insulinspiegel sinkt und zelluläre Recyclingprozesse (Autophagie) werden angeregt.
            </p>
            <p className="article-text">
              Aber das Fasten muss erträglich und angenehm sein. Wenn das Fasten zu starr wird, droht es, den eigentlichen Zweck zu untergraben: ein gesundes und freudvolles Leben zu unterstützen.
            </p>
          </section>

          {/* Image de transition */}
          <div className="article-image-section">
            <img src="/images/interval-fasten-bild-2.jpg" alt="Intervallfasten Praxis" className="article-img" />
          </div>

          {/* Die 14:10-Strategie */}
          <section className="article-section">
            <h2 className="section-title">Die 14:10-Strategie in der Praxis</h2>
            <p className="article-text">
              Ein 14:10-Zeitplan bedeutet, dass Sie alle Ihre Mahlzeiten innerhalb eines 10-Stunden-Fensters zu sich nehmen – zum Beispiel von 8:00 bis 16:00 Uhr, von 9:00 bis 17:00 Uhr oder von 10:00 bis 18:00 Uhr, in Übereinstimmung mit Ihrem zirkadianen Rhythmus. Dieser Ansatz unterstützt die metabolische Gesundheit und ermöglicht gleichzeitig genügend Flexibilität für das reale Leben.
            </p>
            
            <div className="info-box">
              <h3 className="info-title">So legen Sie Ihre Mahlzeiten fest:</h3>
              <ul className="info-list">
                <li>Beginnen Sie 1–2 Stunden nach dem Aufwachen mit dem Essen, damit der Melatonin Spiegel sinkt und das Verdauungssystem vollständig erwacht. Für die meisten Menschen bedeutet dies, dass sie gegen 09:00 Uhr frühstücken.</li>
                <li>Das Abendessen sollte leicht und früh sein, idealerweise gegen 17:00 Uhr, um eine gute Verdauung vor dem Schlafengehen zu ermöglichen. Zwischen Ihrem letzten Bissen und der Schlafenszeit sollten mindestens 3–4 Stunden liegen.</li>
                <li>Seien Sie bei gelegentlichen geselligen Abenden flexibel. Genießen Sie den Moment ohne Schuldgefühle und kehren Sie am nächsten Tag zu Ihrem Rhythmus zurück.</li>
              </ul>
            </div>
          </section>

          {/* Die drei Mahlzeiten */}
          <section className="article-section">
            <h2 className="section-title">Die drei Mahlzeiten des Tages</h2>
            <p className="article-text">
              Beim intermittierenden Fasten geht es nicht um Entbehrungen. Es geht darum, das Gleichgewicht wiederherzustellen und die Mahlzeiten zu schätzen. Dreimal am Tag zu essen sorgt für Struktur, reduziert das Naschen und hilft, die Hungerhormone zu regulieren.
            </p>

            {/* Frühstück */}
            <div className="meal-section">
              <h3 className="meal-title">1. Frühstück (ca. 09:00 Uhr) – leicht, proteinreich und kohlenhydratarm</h3>
              <p className="meal-description">
                Das Frühstück gibt den Ton für Ihren Tag an. Priorisieren Sie Proteine und gute Fette, um das Sättigungsgefühl zu fördern und scharfe Blutzuckerspitzen zu vermeiden.
              </p>
              
              <div className="meal-details">
                <div className="meal-what">
                  <h4>Was es beinhalten kann:</h4>
                  <ul>
                    <li>Eier, Quark, griechischer Joghurt, Tofu oder pflanzliche Alternativen</li>
                    <li>Ein paar Nüsse oder Samen (z.B. gemahlene Leinsamen)</li>
                    <li>Gemüse oder zuckerarmes Obst (z. B. Beeren)</li>
                    <li>Ein heißer Kräutertee oder Kaffee</li>
                  </ul>
                </div>
                
                <div className="meal-avoid">
                  <h4>Was zu vermeiden ist:</h4>
                  <ul>
                    <li>Raffinierter Zucker</li>
                    <li>Weißmehlprodukte</li>
                    <li>Gesüßtes Frühstücksflocken oder Fruchtsäfte</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Mittagessen */}
            <div className="meal-section">
              <h3 className="meal-title">2. Mittagessen (ca. 13:00 Uhr) – Die Hauptmahlzeit</h3>
              <p className="meal-description">
                Das Mittagessen ist Ihre zentrale Mahlzeit des Tages, sowohl ernährungsphysiologisch als auch sozial. Nehmen Sie sich Zeit, um langsam und bewusst zu essen, am besten am Tisch, ohne Bildschirme.
              </p>
              
              <div className="plate-ratio">
                <h4>Ihr Teller sollte sein:</h4>
                <div className="ratio-items">
                  <div className="ratio-item">
                    <span className="ratio-number">1/2</span>
                    <span className="ratio-text">Gemüse (roh oder gekocht)</span>
                  </div>
                  <div className="ratio-item">
                    <span className="ratio-number">1/4</span>
                    <span className="ratio-text">Vollkornprodukte oder andere Kohlenhydrate</span>
                  </div>
                  <div className="ratio-item">
                    <span className="ratio-number">1/4</span>
                    <span className="ratio-text">Proteine (idealerweise pflanzlich) (Hülsenfrüchte, Tofu) oder kleine Portionen Fisch, Eier oder Fleisch</span>
                  </div>
                </div>
                <p className="dessert-note">
                  Ja, ein kleines Dessert ist erlaubt, vor allem wenn es Freude bereitet. Wählen Sie ein Stück dunkle Schokolade, eine gebackene Frucht oder ein paar Löffel hausgemachtes Kompott. Genuss ist wichtig.
                </p>
              </div>
            </div>

            {/* Abendessen */}
            <div className="meal-section">
              <h3 className="meal-title">3. Abendessen (ca. 17:00 Uhr) – leicht und einfach zu verdauen</h3>
              <p className="meal-description">
                Das Abendessen sollte leicht und gemütlich sein. Das Ziel ist es, vor dem Schlafengehen gut zu verdauen, damit Ihr Körper mit Leichtigkeit in das Fastenfenster eintreten kann.
              </p>
              
              <div className="meal-details">
                <div className="meal-what">
                  <h4>Ideale Optionen:</h4>
                  <ul>
                    <li>Eine Suppe aus Gemüse und Kräutern der Saison</li>
                    <li>Ein gemischter Salat mit einem Esslöffel Öl und einigen gekochten Hülsenfrüchten</li>
                    <li>Leicht gedünstetes Gemüse mit einem Spritzer Olivenöl</li>
                  </ul>
                </div>
                
                <div className="meal-avoid">
                  <h4>Vermeiden Sie:</h4>
                  <p>Tierische Proteine oder reichhaltige, fetthaltige Lebensmittel, die die Verdauung oder den Schlaf stören könnten.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section livre de recettes */}
          <section className="article-section cookbook-section">
            <div className="cookbook-content">
              <div className="cookbook-image">
                <img src="/images/interval-fasten-cookbook.jpg" alt="A pleasure a day - Cooking Book" className="cookbook-img" />
              </div>
              <div className="cookbook-info">
                <h3 className="cookbook-title">A pleasure a day – Cooking Book</h3>
                <p className="cookbook-description">
                  Gesunde Küche hält Sie fit und gesund und schützt die natürlichen Grundlagen für alle Lebewesen. Für uns sind gesundes Essen und Fasten zwei Seiten derselben Medaille. Nach dem Fasten ist die Wiederernährung oder Regenerationsphase von entscheidender Bedeutung für den Erfolg der Fastentherapie.
                </p>
                <p className="cookbook-description">
                  Wir haben dieses Kochbuch geschrieben, damit Sie zu Hause eine gesunde und nahrhafte Ernährung genießen können. Unser internationales Team aus Köchen, Ernährungswissenschaftlern und Ärzten hat tägliche Inspiration für köstliche Rezepte zusammengestellt.
                </p>
                <div className="cookbook-cta">
                  <a 
                    href="https://buchinger-wilhelmi-shop.com/en/produkt/a-pleasure-a-day-cooking-book/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="cookbook-link"
                  >
                    <span>Bestellen</span>
                    <span className="cookbook-price">39,00€</span>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Heißhunger und Naschen */}
          <section className="article-section">
            <h2 className="section-title">Heißhunger und Naschen: Was zu tun ist</h2>
            <p className="article-text">
              Wenn zwischen den Mahlzeiten Heißhunger aufkommt, halten Sie inne und fragen Sie sich:
            </p>
            <ul className="article-list">
              <li>Handelt es sich um echten Hunger, oder stecken vielleicht Stress, Langeweile oder emotionale Auslöser dahinter?</li>
              <li>Können Sie die Empfindung einfach beobachten, ohne sofort darauf zu reagieren?</li>
            </ul>
            <p className="article-text">
              Versuchen Sie, einen Kräutertee zu trinken, einen kurzen Spaziergang zu machen oder eine entspannende Aktivität zu unternehmen.
            </p>
            <p className="article-text">
              Wenn das Verlangen überwältigend wird, können Sie zu Ihrem Notfall-Mix greifen:
            </p>
            <div className="emergency-mix">
              <p>Eine kleine Handvoll ungesalzener gemischter Nüsse: Walnüsse, Mandeln, Cashewnüsse, Kürbiskerne.</p>
              <p>Essen Sie langsam, bewusst, ohne Schuldgefühle. Kehren Sie dann zu Ihrem Rhythmus zurück.</p>
            </div>
          </section>

          {/* Schlüsselprinzipien */}
          <section className="article-section">
            <h2 className="section-title">Schlüsselprinzipien für den Erfolg</h2>
            <ul className="principles-list">
              <li>
                <strong>Essen Sie echte Lebensmittel</strong> – Minimal verarbeitet, lokal angebaut, wenn möglich biologisch.
              </li>
              <li>
                <strong>Vermeiden Sie Naschen</strong> – Gönnen Sie Ihrer Verdauung regelmäßig Pausen.
              </li>
              <li>
                <strong>Kochen Sie Ihre eigenen Mahlzeiten</strong> – Entdecken Sie die Freude an der Zubereitung von Speisen neu.
              </li>
              <li>
                <strong>Seien Sie flexibel</strong> – Fasten sollte sich dem Leben anpassen, nicht umgekehrt.
              </li>
              <li>
                <strong>Vertrauen Sie Ihrem Körper</strong> – Er weiß, wann er hungrig ist und wann nicht.
              </li>
            </ul>
          </section>

          {/* Schlussfolgerung */}
          <section className="article-section conclusion">
            <h2 className="section-title">Schlussfolgerung</h2>
            <p className="article-text">
              Fasten ist nicht nur die Abwesenheit von Nahrung. Es ist eine Wiederverbindung mit den natürlichen Rhythmen, eine Möglichkeit, das Sättigungsgefühl, den Geschmack und die einfache Freude an der Nahrung wiederzuentdecken. Das 14:10-Intervallfastenmodell ist in Kombination mit drei vollwertigen Mahlzeiten sowohl effektiv als auch nachhaltig – eine Methode, um Ihre Gesundheit zu fördern und gleichzeitig Ihren Alltag zu berücksichtigen.
            </p>
            <p className="article-text emphasis">
              Denken Sie daran: Erfolg liegt nicht in Perfektion, sondern in Beständigkeit, Achtsamkeit und Freundlichkeit Ihnen gegenüber selbst.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Article; 